import {
  Box,
  Button,
  Code,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Stack,
  StackDivider,
  Tag,
  Text,
  useMediaQuery,
  useToast,
} from "@chakra-ui/react";
import { ethers } from "ethers";
import { useEffect, useRef, useState } from "react";
import Blockies from "react-blockies";
import useMode from "../../hooks/useMode";
import {
  convertTime,
  getDynasty,
  getEpoch,
  getETA,
  getLatestBlockTimestamp,
  initiateWithdraw,
} from "../../utils/contracts";
import { epochMode } from "../../utils/constants";

const InitiateWithdrawDrawer = ({
  isOpen,
  onClose,
  operatorId,
  validatorAddress,
  toggleDrawer,
  unstakeLock,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [epoch, setEpoch] = useState(null);
  const [dynasty, setDynasty] = useState(null);
  const [timestamp, setTimestamp] = useState(0);
  

  const firstField = useRef();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { mode } = useMode();
  const toast = useToast();

  useEffect(() => {
    setTime();
  }, []);

  useEffect(() => {
    fetchTimestamp();
  }, []);

  const fetchTimestamp = async () => {
    const timestamp = await getLatestBlockTimestamp();
    setTimestamp(timestamp);
  };
  

  const setTime = async () => {
    const currentEpoch = await getEpoch();
    const currentDynasty = await getDynasty();
    setEpoch(currentEpoch);
    setDynasty(currentDynasty);
  };

  const { amount, unlockAfter, unlockBefore } = unstakeLock;

  const handleRPCError = (error) => {
    return <Code colorScheme="black">{error.message}</Code>;
  };

  const triggerToast = (title, status = "success", description = null) => {
    toast({
      title,
      description,
      status,
      isClosable: true,
      duration: 5000,
    });
  };

  const initWithdraw = async () => {
    setIsLoading(true);

    const { success, txHash, error } = await initiateWithdraw(operatorId);
    setIsLoading(false);
    console.log({
      success,
      txHash,
      error,
    });
    if (success) {
      triggerToast("Initiate Withdraw transaction successful", "success", null);
      toggleDrawer();
    } else {
      triggerToast(
        "Initiate Withdraw transaction failed",
        "error",
        error?.message && handleRPCError(error)
      );
    }
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        initialFocusRef={firstField}
        onClose={onClose}
        placement={isMobile ? "bottom" : "right"}
      >
        <DrawerOverlay />
        <DrawerContent bg="linear-gradient(111.84deg, rgba(0, 0, 0, 0.94) 59.3%, rgba(26, 31, 55, 0) 100%)">
          <DrawerHeader borderBottomWidth="1px">
            <Tag>Initiate Withdraw</Tag>
            <Flex>
              <Blockies seed={validatorAddress} className="indenticon" />
              <Text>{validatorAddress.slice(0, 20)}</Text>
            </Flex>
          </DrawerHeader>
          <DrawerBody>
            <Stack spacing="24px">
              <Box>
                <Stack
                  divider={<StackDivider />}
                  spacing="4"
                  direction="row"
                  justifyContent="space-around"
                >
                  <Box>
                    <Heading size="xs" textTransform="uppercase">
                      Amount
                    </Heading>
                    <Text pt="2" fontSize="sm">
                      {ethers.utils.formatEther(amount)}
                    </Text>
                  </Box>
                  <Box>
                    <Heading size="xs" textTransform="uppercase">
                      State
                    </Heading>
                    <Text pt="2" fontSize="sm">
                      {mode === -1 ? "-" : epochMode[mode]}
                    </Text>
                  </Box>
                </Stack>
              </Box>
              <Box>
                <Stack
                  divider={<StackDivider />}
                  spacing="4"
                  direction="row"
                  justifyContent="space-around"
                >
                  <Box>
                    <Heading size="xs" textTransform="uppercase">
                      Initiate After
                    </Heading>
                    <Text pt="2" fontSize="sm">
                      {unlockAfter.toNumber()}{" "}
                      {`(ETA: ${getETA(
                        timestamp,
                        unlockAfter,
                        unlockBefore,
                        true
                      )})`}
                    </Text>
                  </Box>
                  <Box>
                    <Heading size="xs" textTransform="uppercase">
                      Initiate before
                    </Heading>
                    <Text pt="2" fontSize="sm">
                      {unlockBefore.toNumber()}{" "}
                      {`(ETA: ${getETA(
                        timestamp,
                        unlockAfter,
                        unlockBefore,
                        false
                      )})`}
                    </Text>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <Button variant="outline" mr={3} onClick={toggleDrawer}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={initWithdraw}
              isLoading={isLoading}
              isDisabled={isLoading}
            >
              Confirm
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default InitiateWithdrawDrawer;
